import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { UploadMultimediaService } from 'src/app/services/upload/upload-multimedia.service';
import { RequestService } from 'src/app/services/request/request.service';

@Component({
  selector: 'app-profile-voice-recorder',
  templateUrl: './profile-voice-recorder.component.html',
  styleUrls: ['./profile-voice-recorder.component.scss'],
})
export class ProfileVoiceRecorderComponent implements OnInit, OnDestroy {
  @Input() profileId?: number;
  @Input() owner = true;
  recording = false;
  uploading = false;
  loadingExisting = true;
  hasSavedMessage = false;
  seconds = 0;
  previewUrl = '';
  private previewIsObjectUrl = false;
  private recorder?: MediaRecorder;
  private stream?: MediaStream;
  private timer?: ReturnType<typeof setInterval>;
  private audioBlob?: Blob;

  constructor(
    private modalController: ModalController,
    private uploadMultimedia: UploadMultimediaService,
    private request: RequestService,
  ) {}

  ngOnInit(): void {
    this.loadExistingMessage();
  }

  ngOnDestroy(): void {
    this.releaseMedia();
    this.revokeLocalPreview();
  }

  close(saved = false): void {
    this.modalController.dismiss({ saved });
  }

  async toggleRecording(): Promise<void> {
    if (this.recording) {
      this.recorder?.stop();
      return;
    }

    this.discard();
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks: BlobPart[] = [];
      this.recorder = new MediaRecorder(this.stream);
      this.recorder.ondataavailable = (event) => {
        if (event.data?.size) chunks.push(event.data);
      };
      this.recorder.onstop = () => {
        this.audioBlob = new Blob(chunks, { type: this.recorder?.mimeType || 'audio/webm' });
        this.previewUrl = URL.createObjectURL(this.audioBlob);
        this.previewIsObjectUrl = true;
        this.hasSavedMessage = false;
        this.recording = false;
        this.stopTimer();
        this.releaseMedia();
      };
      this.recorder.start();
      this.recording = true;
      this.seconds = 0;
      this.timer = setInterval(() => {
        this.seconds += 1;
        if (this.seconds >= 60) this.recorder?.stop();
      }, 1000);
    } catch (error) {
      console.error('Microphone access failed.', error);
    }
  }

  discard(): void {
    this.revokeLocalPreview();
    this.previewUrl = '';
    this.previewIsObjectUrl = false;
    this.hasSavedMessage = false;
    this.audioBlob = undefined;
    this.seconds = 0;
  }

  save(): void {
    if (!this.audioBlob || this.uploading) return;
    this.uploading = true;
    const filename = `profile-voice-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
    this.uploadMultimedia.post(this.audioBlob, filename)
      .then(({ link }) => this.request.createRequestPost('voice_note_instance', { url: link }))
      .then(() => this.close(true))
      .catch((error) => console.error('Unable to save profile voice message.', error))
      .finally(() => this.uploading = false);
  }

  formatTime(): string {
    return `0:${String(this.seconds).padStart(2, '0')}`;
  }

  private stopTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }

  private releaseMedia(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = undefined;
  }

  private loadExistingMessage(): void {
    this.request.createRequestPost('voice_note_instance', {
      get: true,
      profile_id: this.profileId,
    })
      .then((response) => {
        const voiceNote = response?.success;
        const url = typeof voiceNote?.url === 'string' ? voiceNote.url.trim() : '';
        if (!url) return;

        this.previewUrl = url;
        this.hasSavedMessage = true;
      })
      .catch((error) => console.error('Unable to load profile voice message.', error))
      .finally(() => this.loadingExisting = false);
  }

  private revokeLocalPreview(): void {
    if (this.previewIsObjectUrl && this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }
}
